import { action, makeAutoObservable, runInAction } from 'mobx';
import { getAllEnrollmentPeriodsByGroupId } from 'api/operations/enrollmentService';
import { getEnrollsByUserId, getSubscriberByUserIdAndGroupId } from 'api/operations/electionService';
import { bpHistory } from 'global/bpHistory';
import { SUBSCRIBER_CARD_DATA, LegacySubChecklistCard } from 'global/checklists-helpers/SubscriberChecklistHelper';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { isSubscriberV2ChecklistCard, SUBSCRIBER_V2_CHECKLIST_MAPPING } from 'store/flows/subscriber/checklistMapping';
import { redirectToRoute } from 'routes/redirectToRoute';
import { ROUTES_CONFIG_V2 } from 'routes/routesConfigV2';
import { SubChecklistCard, SubscriberTask } from 'types/electionService';
import {
  ChecklistButtonStateText,
  ChecklistCard,
  ChecklistCompletionStatus,
  EnrollmentStage,
} from 'types/enrollmentService';

interface DomainData {
  groupId: string;
}

export interface ChecklistOptionPresenter {
  card: SubChecklistCard;
  title: string;
  text: string;
  buttonState: ChecklistButtonStateText;
  completionStatus: ChecklistCompletionStatus;
  onActionClick: () => void;
}

export class SubscriberTasksModule {
  private _domainData: DomainData;
  private _isLoading = true;
  private _activePeriodId: string | null = null;
  private _isElectionCompleted = false;
  private _subscriberDelegate: SubscriberDelegate | null = null;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get domainData(): DomainData {
    return this._domainData;
  }

  public get subscriberId(): string {
    return this._subscriberDelegate?.subscriber.id || '';
  }

  public get activeSubscriberTasks(): SubscriberTask[] {
    return this._subscriberDelegate?.subscriberTasks.active || [];
  }

  public get inactiveSubscriberTasks(): SubscriberTask[] {
    return this._subscriberDelegate?.subscriberTasks.inactive || [];
  }

  public get showNoActiveTasks(): boolean {
    return (!this._activePeriodId || this._isElectionCompleted) && this.activeSubscriberTasks.length === 0;
  }

  public getTaskChecklistPresenters(entityId: string): ChecklistOptionPresenter[] {
    if (!this._subscriberDelegate) return [];

    return this._subscriberDelegate.getTaskChecklistOptions(entityId).map((option) => {
      const card = option.card;

      if (isSubscriberV2ChecklistCard(card)) {
        const pageConfig = ROUTES_CONFIG_V2[SUBSCRIBER_V2_CHECKLIST_MAPPING[card]];

        return {
          card,
          title: pageConfig.title,
          text: pageConfig.subtitle || '',
          buttonState: option.buttonState,
          completionStatus: option.completionStatus,
          onActionClick: action((): void => {
            this.onStepRedirect(entityId, card);
          }),
        };
      }

      const cardData = SUBSCRIBER_CARD_DATA[card as LegacySubChecklistCard];

      return {
        card,
        title: cardData.title,
        text: cardData.text,
        buttonState: option.buttonState,
        completionStatus: option.completionStatus,
        onActionClick: action((): void => {
          this.onStepRedirect(entityId, card);
        }),
      };
    });
  }

  public onStepRedirect(entityId: string, card: SubChecklistCard): void {
    if (isSubscriberV2ChecklistCard(card)) {
      redirectToRoute(SUBSCRIBER_V2_CHECKLIST_MAPPING[card], {
        groupId: this._domainData.groupId,
        subscriberId: this.subscriberId,
        entityId,
      });
      return;
    }

    const cardData = SUBSCRIBER_CARD_DATA[card];
    if (cardData?.redirectTo) {
      bpHistory.push(
        cardData.redirectTo({
          groupId: this._domainData.groupId,
          entityId,
          subId: this.subscriberId,
        }),
      );
    }
  }

  private async _resolveActivePeriodId(): Promise<string | null> {
    const [periods, enrollsWithSubs] = await Promise.all([
      getAllEnrollmentPeriodsByGroupId(this._domainData.groupId),
      getEnrollsByUserId(),
    ]);

    const activePeriods = periods.filter(
      (period) => period.hasConfirmedProposal && period.stage === EnrollmentStage.ENROLLING,
    );

    if (activePeriods.length > 1) {
      throw new Error('There can be only one active period');
    }

    const period = activePeriods[0];
    if (!period || !enrollsWithSubs?.[period.id]) {
      return null;
    }

    return period.id;
  }

  private async _loadSubscriber(periodId: string): Promise<void> {
    const subscriber = await getSubscriberByUserIdAndGroupId(this._domainData.groupId);
    if (!subscriber) return;

    runInAction(() => {
      this._subscriberDelegate = new SubscriberDelegate({ subscriber, periodId });
    });
  }

  private async _checkElectionForCompleted(periodId: string): Promise<void> {
    await this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId);
    runInAction(() => {
      const electionStatus = this._enrollmentPeriodModule.checklist?.checklistOptions.find(
        (option) => option.card === ChecklistCard.EMPLOYEE_ELECTIONS,
      )?.completionStatus;
      this._isElectionCompleted = electionStatus === ChecklistCompletionStatus.COMPLETED;
    });
  }

  public async getInitialData(): Promise<void> {
    if (!this._domainData.groupId) {
      this._isLoading = false;
      return;
    }

    this._isLoading = true;
    this._isElectionCompleted = false;

    try {
      const activePeriodId = await this._resolveActivePeriodId();

      runInAction(() => {
        this._activePeriodId = activePeriodId;
      });

      await this._loadSubscriber(activePeriodId || '');

      if (activePeriodId) {
        await this._checkElectionForCompleted(activePeriodId);
      }
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  }

  constructor(domainData: DomainData) {
    this._domainData = domainData;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
